/**
 * **The letter** (ADR 0029; `ServiceHours.dc.html` · letter): one page on letterhead — to the
 * program, what it confirms, the shifts as signed, who signed and when, and the code anybody can
 * check at the site's /verify. Every line is a check-in and a check-out, or a chair's "closed
 * at shift end"; nothing on it was typed. A requested letter prints nothing: it has no seal yet.
 */
import type { ReactNode } from 'react';
import { hoursLong, hoursShort } from '@cider/domain/engines';
import { Paper, PrintRoot, formatDate, formatTime } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';
import { usePhase } from '../shell/phase.js';
import { weekday } from '../views/volunteers/parts.js';

interface Line {
  title: string;
  areaName: string;
  inAt: string;
  outAt: string | null;
  minutes: number;
  closedBy: 'booth' | 'chair' | 'import' | null;
}
interface Letter {
  id: string;
  code: string | null;
  state: 'requested' | 'signed' | 'superseded';
  festivalYear: number;
  name: string;
  program: string;
  programContact: string | null;
  minutes: number;
  lines: Line[];
  signedBy: string | null;
  signedAt: string | null;
  supersededBy: string | null;
}

/** The festival day an instant fell on, as the letter writes it — "Fri". */
const dayWord = (at: string): string => weekday(at).slice(0, 3);

export function ServiceLetterPage({ id }: { id: string }): ReactNode {
  const l = useLoad<Letter>(`/volunteers/service-hours/letter/${id}`);
  // The verify page's host, from SITE_ORIGIN — the app prints no host of its own.
  const { siteOrigin } = usePhase();
  if (l.error) return <p role="alert">{l.error}</p>;
  const d = l.data;
  if (!d || siteOrigin === null) return <p>Writing the letter…</p>;
  if (d.state === 'requested' || !d.signedAt)
    return (
      <p role="alert">This letter is not signed yet. It prints once a volunteer chair signs it.</p>
    );
  const first = d.lines[0]?.inAt;
  const last = d.lines[d.lines.length - 1]?.inAt;
  return (
    <PrintRoot
      kind="letter"
      title={`Service hours · ${d.name} · ${d.code ?? ''}`}
      pages={[
        <Paper
          key={d.id}
          masthead="Saint Lawrence Apple Fest"
          submast="Verification of volunteer service"
          stamp={formatDate(d.signedAt, { year: true }).toUpperCase()}
          rows={d.lines.map((x, i) => ({
            key: String(i),
            a: `${dayWord(x.inAt)} · ${x.areaName || x.title}`,
            b: `${formatTime(x.inAt)} – ${x.outAt ? formatTime(x.outAt) : ''}`,
            c: hoursShort(x.minutes),
          }))}
          foot={`Verify this letter at ${new URL(siteOrigin).host}/verify · code ${d.code ?? ''}. Anyone with the code sees the volunteer’s first name, the hours, the date signed, and whether the letter is current or superseded. Nothing else.`}
        >
          <div className="paper-letter">
            <p className="ask-letter__to">
              {d.program}
              {d.programContact ? (
                <>
                  <br />
                  {d.programContact}
                </>
              ) : null}
            </p>
            <p>
              This confirms that <b>{d.name}</b> volunteered <b>{hoursLong(d.minutes)}</b> at Saint
              Lawrence Apple Fest
              {first && last
                ? `, ${formatDate(first)}${first === last ? '' : ` – ${formatDate(last)}`}, ${String(d.festivalYear)}`
                : ` in ${String(d.festivalYear)}`}
              .
            </p>
            <p>
              Each line is a check-in and a check-out, or a chair’s “closed at shift end”. Nothing
              on this letter was typed.
            </p>
            <p className="ask-letter__sign">
              Signed
              <br />
              {d.signedBy ?? 'A volunteer chair'} · Volunteer chair ·{' '}
              {formatDate(d.signedAt, { year: true })}
            </p>
            {d.state === 'superseded' ? (
              <p role="note">
                This letter has been superseded by {d.supersededBy ?? 'a newer letter'}.
              </p>
            ) : null}
          </div>
        </Paper>,
      ]}
    />
  );
}
